"use client"

import { SectionSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/skeleton/SectionSkeleton"
import { SidebarSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/skeleton/SiderbarSkeleton"
import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import type { Article, SectionData } from "../types/new"
import { NewsSection } from "./NewsSection"
import { NewsSidebar } from "./NewsSidebar"
import { toImageSrc } from "@/lib/utils"
import { pickBannerImageFormat } from "@/(admin)/modules/ui-builder/config/lib/post-helpers"
import { BannerData } from "@/types/types"

export type NewsHomeProps = {
  sections: SectionData[] // mỗi mục có categoryId + dữ liệu đã fetch
  sidebar: {
    categoryId: string
    categoryName: string
    articles: Article[]
  }[]
  banner?: BannerData
  isLoading?: boolean
}

export default function NewsHomeLayout({ sections, sidebar, banner, isLoading = false }: NewsHomeProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [sidebarHeight, setSidebarHeight] = useState<number>()

  useEffect(() => {
    const contentEl = contentRef.current
    if (!contentEl) return

    const updateHeight = () => {
      const nextHeight = contentEl.getBoundingClientRect().height
      setSidebarHeight(prev => {
        if (prev === undefined || Math.abs(prev - nextHeight) > 1) {
          return nextHeight
        }
        return prev
      })
    }

    updateHeight()

    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateHeight())
      resizeObserver.observe(contentEl)
    }

    window.addEventListener("resize", updateHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener("resize", updateHeight)
    }
  }, [])

  const sidebarStyle = !isLoading && sidebarHeight ? { maxHeight: sidebarHeight } : undefined
  const bannerImageUrl = pickBannerImageFormat(banner, true, "medium")

  return (
    <div className="mx-auto max-w-[1160px] px-4">
      {/* Grid container: 2 cột tự động có cùng chiều cao */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] border-t border-black/20 lg:items-stretch">
        {/* Cột nội dung - chiếm 2 phần trong tỉ lệ 2:1 */}
        <div ref={contentRef} className="md:border-r border-black/20">
          {isLoading
            ? Array.from({ length: 2 }).map((_, idx) => <SectionSkeleton key={`section-skeleton-${idx}`} />)
            : sections.map(s => <NewsSection key={s.categoryId} data={s} />)}
        </div>
        <div className="md:hidden block w-full">
          {isLoading ? (
            <div className="h-32 w-full rounded bg-neutral-200 animate-pulse" />
          ) : (
            bannerImageUrl && (
              <Image
                src={toImageSrc(bannerImageUrl)}
                alt="banner"
                width={600}
                height={300}
                className="w-full h-auto object-cover"
              />
            )
          )}
        </div>

        {/* Sidebar - chiếm 1 cột, có chiều cao bằng cột trái và scroll khi cần */}
        <div className="flex min-h-0 w-full">
          {isLoading ? (
            <SidebarSkeleton />
          ) : (
            <NewsSidebar tabs={sidebar} className="h-full w-full" style={sidebarStyle} />
          )}
        </div>
      </div>
    </div>
  )
}
